import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { CreditCard, Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const emptyForm = {
  bank_name: '',
  account_number: '',
  account_holder: '',
  label: '',
  is_active: true,
};

const BankAccountsPage = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('manage_bank_accounts');

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/bank-accounts');
      setAccounts(data);
    } catch (error) {
      console.error(error);
      toast.error('Gagal memuat daftar rekening');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditMode(false);
    setEditingId(null);
    setForm(emptyForm);
    setShowDialog(true);
  };

  const openEdit = (acc) => {
    setEditMode(true);
    setEditingId(acc.id);
    setForm({
      bank_name: acc.bank_name || '',
      account_number: acc.account_number || '',
      account_holder: acc.account_holder || '',
      label: acc.label || '',
      is_active: acc.is_active !== false,
    });
    setShowDialog(true);
  };

  const handleDelete = async (acc) => {
    if (!window.confirm(
      `Hapus rekening "${acc.bank_name} ${acc.account_number}"?\n\n` +
      `Jika rekening ini sudah pernah dipakai di invoice, hapus akan gagal — ` +
      `nonaktifkan saja (uncheck 'Aktif' di menu Edit) supaya tidak muncul di dropdown.`
    )) return;
    try {
      await api.delete(`/bank-accounts/${acc.id}`);
      toast.success('Rekening berhasil dihapus');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus rekening');
    }
  };

  const handleSave = async () => {
    const bank_name = (form.bank_name || '').trim();
    const account_number = (form.account_number || '').trim();
    const account_holder = (form.account_holder || '').trim();

    if (!bank_name || !account_number || !account_holder) {
      toast.error('Nama bank, nomor rekening, dan nama pemilik wajib diisi');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        bank_name,
        account_number,
        account_holder,
        label: (form.label || '').trim() || null,
        is_active: !!form.is_active,
      };
      if (editMode) {
        await api.put(`/bank-accounts/${editingId}`, payload);
        toast.success('Rekening berhasil diperbarui');
      } else {
        await api.post('/bank-accounts', payload);
        toast.success('Rekening berhasil ditambahkan');
      }
      setShowDialog(false);
      setForm(emptyForm);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan rekening');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="bank-accounts-page">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Master Data</p>
            <h1 className="text-4xl font-medium tracking-tight">Daftar Rekening</h1>
            <p className="text-muted-foreground mt-1">
              Kelola rekening bank tujuan transfer. Rekening aktif akan muncul di dropdown saat generate invoice.
            </p>
          </div>
          {canManage && (
            <Button
              onClick={openAdd}
              className="bg-primary hover:bg-primary/90"
              data-testid="add-bank-account-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Tambah Rekening
            </Button>
          )}
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          </div>
        ) : accounts.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center">
              <CreditCard className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">
                Belum ada rekening. {canManage ? 'Klik "Tambah Rekening" untuk mulai.' : ''}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {accounts.map((acc) => (
              <Card
                key={acc.id}
                className={`border-border hover:-translate-y-1 transition-transform ${!acc.is_active ? 'opacity-60' : ''}`}
                data-testid={`bank-account-${acc.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex items-center gap-2">
                      {!acc.is_active && (
                        <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 text-xs">
                          Non-aktif
                        </Badge>
                      )}
                      {acc.is_active && (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300 text-xs">
                          Aktif
                        </Badge>
                      )}
                    </div>
                  </div>

                  <h3 className="font-medium tracking-tight text-lg">{acc.bank_name}</h3>
                  <p className="text-lg font-mono mt-1">{acc.account_number}</p>
                  <p className="text-sm text-muted-foreground mt-1">a.n. {acc.account_holder}</p>

                  {acc.label && (
                    <p className="text-xs text-muted-foreground italic mt-2">{acc.label}</p>
                  )}

                  {canManage && (
                    <div className="flex items-center gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => openEdit(acc)}
                        data-testid={`edit-bank-account-${acc.id}`}
                      >
                        <Edit className="w-3 h-3 mr-2" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(acc)}
                        className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                        data-testid={`delete-bank-account-${acc.id}`}
                        title="Hapus rekening"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit Rekening' : 'Tambah Rekening'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nama Bank *</Label>
                <Input
                  placeholder="BCA / Mandiri / BNI, dll"
                  value={form.bank_name}
                  onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                  data-testid="bank-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Nomor Rekening *</Label>
                <Input
                  placeholder="1234567890"
                  value={form.account_number}
                  onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                  data-testid="account-number-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Nama Pemilik Rekening *</Label>
                <Input
                  placeholder="PT LeGreen Indonesia"
                  value={form.account_holder}
                  onChange={(e) => setForm({ ...form, account_holder: e.target.value })}
                  data-testid="account-holder-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Label / Keterangan (opsional)</Label>
                <Input
                  placeholder="Contoh: Rekening Operasional Jakarta"
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  data-testid="account-label-input"
                />
                <p className="text-xs text-muted-foreground">
                  Membantu identifikasi jika ada beberapa rekening dari bank yang sama.
                </p>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                <div>
                  <p className="text-sm font-medium">Aktif</p>
                  <p className="text-xs text-muted-foreground">
                    Rekening non-aktif tidak muncul di dropdown pembuatan invoice.
                  </p>
                </div>
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                  data-testid="account-is-active-switch"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)} disabled={saving}>Batal</Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-primary hover:bg-primary/90"
                data-testid="save-bank-account-button"
              >
                {saving ? 'Menyimpan...' : (editMode ? 'Simpan Perubahan' : 'Simpan')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default BankAccountsPage;
